// The donor's single-column, removable sort, as arithmetic.
//
// Every modsy table goes through react-ui's `<Table>`, which hands MRT
// `enableSortingRemoval` and `enableMultiSort={false}` (`react-ui/src/table/
// table.tsx:169-175`). TanStack's cycle for a column is then none → its first
// direction → the other → none again (`RowSorting.js:161-175`), and a click on
// another column replaces the sort rather than adding to it. The shared sort
// control (`components/ui/mui-table-sort.tsx`) draws it and the pages own the
// state; this is the one place the steps are spelled, so the catalog's Usage
// column and the communities list's six share them.
//
// **The first direction is the caller's.** TanStack derives it from the data —
// `getAutoSortDir` reads the first filtered row, offering ascending for a
// string and descending for anything else, an empty page included
// (`RowSorting.js:60-67`) — and the donor sets `sortDescFirst` nowhere. A
// server-sorted page knows its own columns, so it says which, rather than this
// guessing from a row.

/** How a column is ordered, in `aria-sort`'s words. */
export type SortDirection = "ascending" | "descending" | "none";

/** The direction an unsorted column offers first. */
export type FirstSortDirection = Exclude<SortDirection, "none">;

/** The direction a click on a column's sort control moves it to. */
export function nextSortDirection(
  current: SortDirection,
  first: FirstSortDirection,
): SortDirection {
  if (current === "none") return first;
  if (current === first) return first === "descending" ? "ascending" : "descending";
  return "none";
}

/**
 * What the sort control's tooltip — and its accessible name — says, in MRT's
 * words (`index.esm.js:1380-1383`, chosen at `:3470-3478`). The tense carries
 * the state: "Sorted by" reports the ordering in force, "Sort by" describes the
 * click on offer, which on an unsorted column is its first direction.
 */
export function sortHint(label: string, direction: SortDirection, first: FirstSortDirection) {
  if (direction === "none") return `Sort by ${label} ${first}`;
  return `Sorted by ${label} ${direction}`;
}
